// Interest management — the geometry + leave-edge bookkeeping behind
// area-of-interest culling. This is the framework half of the
// `set_interest` / `relay_interested` builtins: the script declares each
// slot's interest region, the HOST owns recipient selection (it knows the
// regions) and the leave signal, so the server stays game-agnostic — it
// culls fan-out by generic geometry, never by parsing game packets.
//
// One implementation, used by the Node server's ScriptInstance AND read by
// the fidelity rig to validate against the SAME declared regions the host
// culled with — the single source of truth that makes the rig AoI-aware
// without re-deriving any game predicate. See docs/interest-management.md.
// The entire game-specific relevance predicate, reduced to declared
// geometry: same area AND boxes intersect. A null region (slot never set
// its interest, e.g. still in the level intro) overlaps nothing — matching
// "not in the level yet."
export function regionsOverlap(a, b) {
    if (!a || !b)
        return false;
    if (a.area !== b.area)
        return false;
    return a.x < b.x + b.w && a.x + a.w > b.x
        && a.y < b.y + b.h && a.y + a.h > b.y;
}
// Per-instance interest state + relay/leave bookkeeping. Holds each slot's
// declared region and a `relayed` matrix tracking which (receiver, subject)
// pairs are currently being delivered, so the host can fire `peer_left`
// exactly once on each relevant→irrelevant edge.
export class InterestTable {
    maxClients;
    regions;
    // relayed[receiver * maxClients + subject] — true while we are currently
    // delivering `subject` to `receiver`. The edge from true→false is the
    // leave signal.
    relayed;
    constructor(maxClients) {
        this.maxClients = maxClients;
        this.regions = new Array(maxClients).fill(null);
        this.relayed = new Array(maxClients * maxClients).fill(false);
    }
    // Record (or replace) a slot's interest region for this tick.
    set(slot, area, x, y, w, h) {
        if (slot < 0 || slot >= this.maxClients)
            return;
        // Normalize the box dimensions: a script-supplied negative or non-finite
        // w/h makes the AABB test (`a.x + a.w > b.x`) put the right/bottom edge
        // LEFT of the left/top edge, so regionsOverlap returns garbage and a peer
        // is silently culled (or kept) wrongly — which can also suppress the
        // peer_left edge. Treat a malformed box as empty (overlaps nothing).
        const ww = Number.isFinite(w) && w > 0 ? w : 0;
        const hh = Number.isFinite(h) && h > 0 ? h : 0;
        this.regions[slot] = { area, x, y, w: ww, h: hh };
    }
    // Read one slot's region (null if never set / cleared).
    region(slot) {
        if (slot < 0 || slot >= this.maxClients)
            return null;
        return this.regions[slot] ?? null;
    }
    // Snapshot of every slot's region — the rig reads this each frame to
    // compute the SAME overlap the host culled with (its expected-visible
    // gate). Returns a fresh array; entries are the live region objects.
    snapshot() {
        return this.regions.slice();
    }
    // Reset a slot in BOTH directions (as receiver and as subject) — clear
    // its region and every relay edge touching it. Call on connect (so a
    // reused slot doesn't inherit the previous occupant's relevance) and
    // after a disconnect's leave signals have been emitted.
    clearSlot(slot) {
        if (slot < 0 || slot >= this.maxClients)
            return;
        this.regions[slot] = null;
        for (let k = 0; k < this.maxClients; k++) {
            this.relayed[slot * this.maxClients + k] = false; // slot as receiver
            this.relayed[k * this.maxClients + slot] = false; // slot as subject
        }
    }
    // Fan-out decision for one subject against the currently-connected
    // receivers. `connected(slot)` reports which slots are live. Returns:
    //   - `deliver`: receivers whose interest overlaps the subject → send to them.
    //   - `left`: receivers that WERE getting the subject but no longer overlap
    //     (it scrolled out, or its region went null) → emit peer_left exactly once.
    // Updates the relayed matrix. Call once per subject per tick.
    relay(subject, connected) {
        const deliver = [];
        const left = [];
        // Bounds-guard the subject like set/region/clearSlot do: the matrix
        // index is `r * maxClients + subject`, so an out-of-range subject would
        // write past the relayed[] matrix (growing it sparsely) and corrupt the
        // (receiver, subject) edge accounting.
        if (subject < 0 || subject >= this.maxClients)
            return { deliver, left };
        const subjReg = this.region(subject);
        for (let r = 0; r < this.maxClients; r++) {
            if (r === subject)
                continue;
            if (!connected(r))
                continue;
            const idx = r * this.maxClients + subject;
            const rel = subjReg !== null && regionsOverlap(subjReg, this.regions[r]);
            if (rel) {
                deliver.push(r);
                this.relayed[idx] = true;
            }
            else if (this.relayed[idx]) {
                left.push(r);
                this.relayed[idx] = false;
            }
        }
        return { deliver, left };
    }
    // The subject is gone (disconnected / timed out / killed). Return the
    // connected receivers currently getting it — emit peer_left to each — and
    // clear those edges. Does NOT clear the subject's own region; the caller
    // pairs this with clearSlot for a full reset.
    subjectGone(subject, connected) {
        const left = [];
        if (subject < 0 || subject >= this.maxClients)
            return left;
        for (let r = 0; r < this.maxClients; r++) {
            if (r === subject)
                continue;
            const idx = r * this.maxClients + subject;
            if (this.relayed[idx]) {
                this.relayed[idx] = false;
                if (connected(r))
                    left.push(r);
            }
        }
        return left;
    }
}
//# sourceMappingURL=interest.js.map